import * as React from "react";
import { clsx } from "clsx";
import { Loader2, Pencil } from "lucide-react";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { SnapshotGridPortal } from "@/modules/snapshots/grid/portal";
import {
  getSnapshotCellPreview,
  getTypedSnapshotCellDisplayText,
  isSnapshotCellFailed,
  isSnapshotCellPending,
} from "@/modules/snapshots/grid/cellText";
import { useToggle } from "react-use";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { Tooltip } from "@/components/ui/tooltip";
import { useGetDirectoryUserQuery } from "@/api/users-admin";
import { TrafficLightCell } from "@/modules/snapshots/grid/table/TrafficLightCell";
import { ReviewVerdictBadge } from "@/modules/snapshots/grid/reviewVerdictBadge";
import { SNAPSHOT_GRID_DATA_COLUMN_WIDTH, getSnapshotGridColumnStyle } from "./layout";
import type { SnapshotGridCellModel, SnapshotGridRowModel } from "@/modules/snapshots/grid/rowModel";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
  header: string;
  snapshotCollection: SnapshotCollection;
  onRowClick?: () => void;
  businessReportOutcomeResultWithEvent: ReadonlySet<string>;
  isHighlighted?: boolean;
  columnWidth?: number;
  interactionDisabled?: boolean;
  canEditResult?: boolean;
}

export const SnapshotGridTableRowResult: React.FC<Props> = ({
  businessReportOutcome,
  header,
  snapshotCollection,
  onRowClick,
  businessReportOutcomeResultWithEvent,
  isHighlighted = false,
  columnWidth = SNAPSHOT_GRID_DATA_COLUMN_WIDTH,
  interactionDisabled = false,
  canEditResult = true,
}) => {
  const result = businessReportOutcome.result.find(row => row.name === header);
  const textToShow = result ? getSnapshotCellPreview(getTypedSnapshotCellDisplayText(result)) : "";
  const [isOpen, toggleIsOpen] = useToggle(false);
  const { formatDate, formatDateTime } = useDateFormatter();
  const { data: editedByUser } = useGetDirectoryUserQuery(result?.edited_by_id ?? "", {
    skip: !result?.edited_by_id,
  });
  const editorName = editedByUser ? `${editedByUser.name} ${editedByUser.surname}`.trim() : null;

  const handleRowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (interactionDisabled) return;
    onRowClick?.();
    toggleIsOpen();
  };

  const handleClosePortal = () => {
    toggleIsOpen();
  };

  const hasEvent = result ? businessReportOutcomeResultWithEvent.has(result.id) : false;

  React.useEffect(() => {
    if (interactionDisabled && isOpen) {
      toggleIsOpen(false);
    }
  }, [interactionDisabled, isOpen, toggleIsOpen]);

  if (!result) {
    return (
      <td
        className={clsx(
          "overflow-hidden transition-colors group-hover:bg-ivory-mist-400",
          isHighlighted && "bg-blue-100",
        )}
        style={getSnapshotGridColumnStyle(columnWidth)}
      />
    );
  }

  const isGenerating = isSnapshotCellPending(result);
  const generatingCell = (
    <span className="flex min-w-0 flex-1 items-center gap-2 text-xs text-gray-400">
      <Loader2 className="h-3 w-3 animate-spin" />
      Generating…
    </span>
  );

  return (
    <td
      className={clsx(
        "overflow-hidden p-0 text-sm transition-colors group-hover:bg-ivory-mist-400",
        interactionDisabled ? "cursor-default" : "cursor-pointer",
        isHighlighted && "bg-blue-100",
      )}
      style={getSnapshotGridColumnStyle(columnWidth)}
      valign="middle"
    >
      <div className="flex min-w-0 items-center overflow-hidden py-2 pl-4 pr-2" onClick={handleRowClick}>
        {hasEvent && (
          <span
            className="mr-2 inline-flex h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-coral"
            title="Has event"
          />
        )}
        {result.edited_at && (
          <Tooltip
            text={`Edited${editorName ? ` by ${editorName}` : ""} on ${formatDateTime(result.edited_at)}`}
            delayDuration={50}
          >
            <Pencil className="mr-2 h-3 w-3 flex-shrink-0 text-gray-400" />
          </Tooltip>
        )}
        {result.review?.verdict && (
          <ReviewVerdictBadge
            verdict={result.review.verdict}
            shortExplanation={result.review.short_explanation}
            compact
            className="mr-2"
          />
        )}
        {isSnapshotCellFailed(result) ? (
          <span className="flex min-w-0 flex-1 items-center text-xs text-red-600">
            <span className="truncate" title={result.error_message ?? undefined}>
              Failed
            </span>
          </span>
        ) : result.rating ? (
          <TrafficLightCell rating={result.rating} />
        ) : result.is_date ? (
          result?.date ? (
            formatDate(result.date)
          ) : isGenerating ? (
            generatingCell
          ) : (
            <span className="min-w-0 flex-1 truncate">{textToShow || "N/A"}</span>
          )
        ) : isGenerating ? (
          generatingCell
        ) : (
          <span className="min-w-0 flex-1 truncate">{textToShow}</span>
        )}
      </div>
      {isOpen && !!result && (
        <SnapshotGridPortal
          isOpen={isOpen}
          snapshotCollectionId={businessReportOutcome.snapshot_collection_id ?? snapshotCollection.id}
          setIsOpen={handleClosePortal}
          businessReportOutcome={businessReportOutcome}
          result={result}
          canEditResult={canEditResult}
        />
      )}
    </td>
  );
};

interface PipelineProps {
  row: SnapshotGridRowModel;
  cell: SnapshotGridCellModel | null;
  fieldName: string;
  onOpenCell?: (row: SnapshotGridRowModel, cell: SnapshotGridCellModel | null) => void;
}

const getPipelineCellValue = (cell: SnapshotGridCellModel | null): string => {
  if (cell?.issue) return "Unavailable";
  if (cell?.rating) return cell.rating;
  if (cell?.date) return cell.date;
  return cell?.value === "" || cell === null ? "N/A" : cell.value;
};

export const SnapshotGridTablePipelineRowResult: React.FC<PipelineProps> = ({ row, cell, fieldName, onOpenCell }) => {
  const value = getPipelineCellValue(cell);
  const isFailed = Boolean(cell?.issue);
  const accessibleLabel = `${fieldName}: ${value}${isFailed ? " (failed summary)" : ""}`;
  const truncatedValue = value.length > 64 ? `${value.slice(0, 63)}…` : value;

  const handleOpen = () => onOpenCell?.(row, cell);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleOpen();
  };

  return (
    <td
      className={clsx(
        "overflow-hidden border-b border-inherit p-0 text-sm",
        onOpenCell
          ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-coral"
          : "cursor-default",
      )}
      tabIndex={onOpenCell ? 0 : undefined}
      aria-label={accessibleLabel}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
    >
      <div className={clsx("min-w-0 truncate px-4 py-2", isFailed && "font-medium text-blue-900")} title={value}>
        {truncatedValue}
      </div>
    </td>
  );
};
